import React, { useEffect, useState } from 'react';
import { usePodsStore } from '../stores/usePodsStore';
import { PodCard, PodDetailSheet, CreatePodModal } from '../components/pods';
import { BottomNav } from '../components/navigation';
import { Users } from 'lucide-react';
import './PodsScreen.css';

const PodsScreen: React.FC = () => {
  const {
    pods, isLoading, isLoadingDetail, activePod, error,
    fetchPods, fetchPodDetail,
    createPod, renamePod, deletePod,
    inviteMember, leavePod, removeMember, acceptInvite,
    clearError,
  } = usePodsStore();

  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    fetchPods();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePodClick = async (podId: string) => {
    await fetchPodDetail(podId);
    setShowDetail(true);
  };

  const handleCreate = async (name: string, emails: string[]) => {
    await createPod(name, emails);
  };

  const handleAcceptInvite = async (podId: string) => {
    await acceptInvite(podId);
    await fetchPods();
  };

  const handleDeclineInvite = async (podId: string) => {
    await leavePod(podId); // Decline = leave from pending state
  };

  // Split pods by membership status
  const pendingPods = pods.filter(p => p.my_status === 'pending');
  const activePods = pods.filter(p => p.my_status === 'active');

  return (
    <div className="pods-screen">
      <div className="pods-content">
        {/* Header */}
        <div className="pods-header">
          <h1 className="pods-title">Your Pods</h1>
          <button className="pods-create-btn" onClick={() => setShowCreate(true)}>
            Create Pod
          </button>
        </div>

        {error && (
          <div className="pods-error" onClick={clearError}>
            {error} <span className="pods-error-dismiss">Dismiss</span>
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading && pods.length === 0 && (
          <div className="pods-loading">
            <div className="pods-skeleton" />
            <div className="pods-skeleton" />
            <div className="pods-skeleton" />
          </div>
        )}

        {/* Empty state */}
        {!isLoading && pods.length === 0 && (
          <div className="pods-empty">
            <div className="pods-empty-icon">
              <Users size={28} />
            </div>
            <h2 className="pods-empty-title">Create your first Pod</h2>
            <p className="pods-empty-desc">
              A Pod is the group you plan with. Create one for your family, friends, or anyone you share plans with regularly.
            </p>
            <button className="pods-empty-cta" onClick={() => setShowCreate(true)}>
              Create a Pod
            </button>
          </div>
        )}

        {/* Pending invitations */}
        {pendingPods.length > 0 && (
          <div className="pods-section">
            <span className="pods-section-label">Invitations</span>
            <div className="pods-list">
              {pendingPods.map(pod => (
                <PodCard
                  key={pod.id}
                  pod={pod}
                  onClick={() => {}}
                  onAccept={() => handleAcceptInvite(pod.id)}
                  onDecline={() => handleDeclineInvite(pod.id)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Active pods */}
        {activePods.length > 0 && (
          <div className="pods-list">
            {activePods.map(pod => (
              <PodCard
                key={pod.id}
                pod={pod}
                onClick={() => handlePodClick(pod.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Pod Modal */}
      {showCreate && (
        <CreatePodModal
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}

      {/* Pod Detail Sheet */}
      {showDetail && activePod && (
        <PodDetailSheet
          pod={activePod}
          isLoading={isLoadingDetail}
          onClose={() => setShowDetail(false)}
          onInvite={(email) => inviteMember(activePod.id, email)}
          onLeave={async () => { await leavePod(activePod.id); setShowDetail(false); }}
          onRemoveMember={(userId) => removeMember(activePod.id, userId)}
          onRename={(name) => renamePod(activePod.id, name)}
          onDelete={async () => { await deletePod(activePod.id); setShowDetail(false); }}
        />
      )}

      <BottomNav activeItem="pods" />
    </div>
  );
};

export default PodsScreen;
